/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/controller/useInboxDerivedRuntime.ts
 * FILE_ID: EMAIL-INBOX-CONTROLLER-DERIVED-001
 * PURPOSE: Derive rows, watchdog, suggestions, prefetch, search, and telemetry.
 */

/* eslint-disable react-hooks/exhaustive-deps -- Preserve pre-extraction hook dependencies. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  createSearchQueryResolvedEmitter,
  getSearchRankParam,
} from '@quotech/ui'
import { useApiAuth } from '../../../../_internal/components/ApiAuthGate'
import {
  useEmailBodyPrefetch,
} from '../../../hooks/useEmails'
import { useInboxSuggestions } from '../../../hooks/useInboxSuggestions'
import { useLiveSearch } from '../../../hooks/useLiveSearch'
import {
  countDistinctMailboxes,
  dedupEmails,
  mailboxGroupKeyOf,
  sortEmailsByMailbox,
} from '../../../lib/dedup'
import { sortEmailsBy } from '../../../lib/sortEmails'
import { setShellBackInterceptor } from '../../../lib/shellBackInterceptor'
import { watchdogRemainingMs } from '../../../lib/watchdogClock'
import type { useInboxDataRuntime } from './useInboxDataRuntime'
import type { useInboxPaneRuntime } from './useInboxPaneRuntime'
import type { useInboxState } from './useInboxState'

type InboxState = ReturnType<typeof useInboxState>
type InboxData = ReturnType<typeof useInboxDataRuntime>
type InboxPane = ReturnType<typeof useInboxPaneRuntime>

export function useInboxDerivedRuntime(
  state: InboxState,
  data: InboxData,
  pane: InboxPane,
) {
  const { session } = useApiAuth()
  const currentUserId = useMemo(() => {
    const raw = session?.user?.id
    if (typeof raw === 'number') return raw
    if (typeof raw === 'string') {
      const parsed = Number.parseInt(raw, 10)
      return Number.isFinite(parsed) ? parsed : null
    }
    return null
  }, [session?.user?.id])
  const currentUserEmail = session?.user?.email ?? null
  const hasMultipleMailboxes = state.emailAccounts.length > 1
  const dedupedEmails = useMemo(() => {
    const sorted = sortEmailsBy(
      dedupEmails(data.emails, currentUserId),
      state.sortMode,
    )
    return state.groupByMailbox ? sortEmailsByMailbox(sorted) : sorted
  }, [
    data.emails,
    currentUserId,
    state.groupByMailbox,
    state.sortMode,
  ])
  const mailboxGroupCounts = useMemo<Record<string, number>>(() => {
    if (!state.groupByMailbox) return {}
    const counts: Record<string, number> = {}
    for (const email of dedupedEmails) {
      const key = mailboxGroupKeyOf(email)
      counts[key] = (counts[key] || 0) + 1
    }
    return counts
  }, [state.groupByMailbox, dedupedEmails])
  const distinctMailboxCount = useMemo(
    () => countDistinctMailboxes(dedupedEmails),
    [dedupedEmails],
  )
  const canGroupByMailbox = distinctMailboxCount > 1

  const LOAD_WATCHDOG_MS = 20_000
  const SYNC_WATCHDOG_MS = 45_000
  const watchdogKey =
    `qh-inbox-watchdog-armed-at:${currentUserId ?? 'anon'}`
  const clearWatchdogClock = useCallback(() => {
    try { sessionStorage.removeItem(watchdogKey) } catch { /* no storage */ }
  }, [watchdogKey])
  const [loadWatchdogTripped, setLoadWatchdogTripped] = useState(false)
  useEffect(() => {
    const showingZeroRowSpinner =
      dedupedEmails.length === 0
      && (data.listLoading || data.isAutoSyncing)
    if (!showingZeroRowSpinner) {
      setLoadWatchdogTripped(false)
      clearWatchdogClock()
      return
    }
    const windowMs =
      data.listLoading && !data.isAutoSyncing
        ? LOAD_WATCHDOG_MS
        : SYNC_WATCHDOG_MS
    let armedAt = 0
    try {
      armedAt = Number(sessionStorage.getItem(watchdogKey) || '0')
      if (!armedAt) {
        armedAt = Date.now()
        sessionStorage.setItem(watchdogKey, String(armedAt))
      }
    } catch {
      armedAt = Date.now()
    }
    const remaining = watchdogRemainingMs(
      armedAt,
      Date.now(),
      windowMs,
    )
    if (remaining <= 0) {
      setLoadWatchdogTripped(true)
      return
    }
    const timer = window.setTimeout(
      () => setLoadWatchdogTripped(true),
      remaining,
    )
    return () => window.clearTimeout(timer)
  }, [
    data.listLoading,
    data.isAutoSyncing,
    dedupedEmails.length,
    watchdogKey,
    clearWatchdogClock,
  ])

  const { map: suggestionsMap, dismiss: dismissSuggestion } =
    useInboxSuggestions(dedupedEmails)
  const handleConfirmSuggestion = useCallback(
    async (emailId: string, jobId: number, jobLabel: string) => {
      data.setSuggestionActionId(emailId)
      try {
        const result = await data.linkToJob(emailId, jobId)
        if (result) {
          data.toast(`Linked to ${jobLabel}`, 'success')
          data.refetch()
        } else {
          data.toast(
            'Could not link email - try the Link to Job modal',
            'error',
          )
        }
      } catch (error) {
        data.toast(
          `Could not link: ${(error as Error).message || 'error'}`,
          'error',
        )
      } finally {
        data.setSuggestionActionId(null)
      }
    },
    [
      data.linkToJob,
      data.toast,
      data.refetch,
      data.setSuggestionActionId,
    ],
  )
  const handleRejectSuggestion = useCallback(
    async (emailId: string, jobId: number, jobLabel: string) => {
      data.setSuggestionActionId(emailId)
      try {
        const result = await data.rejectSuggestion(emailId, jobId)
        if (result) {
          data.toast(`Dismissed suggestion (${jobLabel})`, 'success')
          dismissSuggestion(emailId)
        } else {
          data.toast('Could not dismiss suggestion - try again', 'error')
        }
      } catch (error) {
        data.toast(
          `Could not dismiss: ${(error as Error).message || 'error'}`,
          'error',
        )
      } finally {
        data.setSuggestionActionId(null)
      }
    },
    [
      data.rejectSuggestion,
      data.toast,
      dismissSuggestion,
      data.setSuggestionActionId,
    ],
  )
  const prefetchIds = useMemo(
    () => dedupedEmails.map((email) => email.id),
    [dedupedEmails],
  )
  useEmailBodyPrefetch(prefetchIds, {
    skipId: state.selectedEmailId,
    topN: 10,
  })
  const autoOpenedRef = useRef(false)
  const filterSig = useMemo(
    () => JSON.stringify({
      selectedCategories: state.selectedCategories,
      isReadFilter: state.isReadFilter,
      isStarredFilter: state.isStarredFilter,
      allocationFilter: state.allocationFilter,
      jobIdFilter: state.jobIdFilter,
      jobIdsIn: state.jobIdsIn,
      mailboxKeysExplicit: state.mailboxKeysExplicit,
      searchQuery: state.debouncedSearchQuery,
      fromAddressContains: state.fromAddressContains,
      companyDomain: state.companyDomain,
      receivedAtGte: state.receivedAtGte,
      receivedAtLte: state.receivedAtLte,
      hasAttachments: state.hasAttachments,
      hasCalendarEvent: state.hasCalendarEvent,
    }),
    [
      state.selectedCategories, state.isReadFilter, state.isStarredFilter,
      state.allocationFilter, state.jobIdFilter, state.jobIdsIn,
      state.mailboxKeysExplicit, state.debouncedSearchQuery,
      state.fromAddressContains, state.companyDomain, state.receivedAtGte,
      state.receivedAtLte, state.hasAttachments, state.hasCalendarEvent,
    ],
  )
  useEffect(() => {
    autoOpenedRef.current = false
  }, [filterSig])
  useEffect(() => {
    if (
      autoOpenedRef.current
      || state.selectedEmailId
      || dedupedEmails.length === 0
      || pane.isNarrow
    ) {
      return
    }
    autoOpenedRef.current = true
    state.setSelectedEmailId(dedupedEmails[0].id)
  }, [
    dedupedEmails,
    state.selectedEmailId,
    state.setSelectedEmailId,
    pane.isNarrow,
  ])
  const liveSearch = useLiveSearch({
    query: state.debouncedSearchQuery,
    searchActive: !!state.debouncedSearchQuery.trim(),
    localResultCount: dedupedEmails.length,
    localLoading: data.listLoading,
  })
  const searchTelemetryEmitRef = useRef(
    createSearchQueryResolvedEmitter('email-inbox'),
  )
  const activeSearchFreeText =
    state.parsedDebouncedSearch.freeText || ''
  useEffect(() => {
    if (data.listLoading || data.refreshing) return
    searchTelemetryEmitRef.current({
      query: activeSearchFreeText,
      resultCount: dedupedEmails.length,
      rankParamSent:
        activeSearchFreeText && getSearchRankParam() === '1'
          ? true
          : undefined,
    })
  }, [
    activeSearchFreeText,
    dedupedEmails.length,
    data.listLoading,
    data.refreshing,
  ])
  useEffect(() => {
    if (!pane.isNarrow || !state.selectedEmailId) return
    return setShellBackInterceptor(() => {
      state.closeSelectedEmail()
      return true
    })
  }, [
    pane.isNarrow,
    state.selectedEmailId,
    state.closeSelectedEmail,
  ])

  return {
    currentUserId, currentUserEmail, hasMultipleMailboxes, dedupedEmails,
    mailboxGroupCounts, distinctMailboxCount, canGroupByMailbox,
    clearWatchdogClock, loadWatchdogTripped, setLoadWatchdogTripped,
    suggestionsMap, dismissSuggestion, handleConfirmSuggestion,
    handleRejectSuggestion, liveSearch,
  }
}
